import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Inner from '../../components/Inner'
import Button from '../../components/Button'
import SlideBanner from './SlideBanner'
import { routes } from '../../routes'
import axios from 'axios'
import { apiURL } from '../../config'

function Main() {
    const { t } = useTranslation()
	const [email, setEmail] = useState("")
    const [message, setMessage] = useState("")

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            await axios.post(`${apiURL}/customers/`, {
				email,
				agrees_to_ads: true,
			})
            setMessage(t("Thank you for subscribing!"))
            setEmail("")
        } catch {
            setMessage(t("Something went wrong. Please try again."))
        }
    }

    return (
        <>
			<Helmet>
				<title>Eduard Belsky | Online Art Gallery</title>
				<meta name="description" content="Discover and buy original paintings by Eduard Belsky. Explore the catalog, view works in AR, and subscribe for updates." />
				<link rel="canonical" href="https://eduardbelsky.com/" />
				<meta name="robots" content="index, follow" />
				<meta property="og:title" content="Eduard Belsky | Online Art Gallery" />
				<meta property="og:description" content="Discover and buy original paintings by Eduard Belsky. Explore the catalog, view works in AR, and subscribe for updates." />
				{/* <meta property="og:image" content="https://eduardbelsky.com/images/og-home.jpg" /> */}
				<meta property="og:image" content="%PUBLIC_URL%/logo192.png" />
				<meta property="og:url" content="https://eduardbelsky.com/" />
			</Helmet>
			<h1 className="sr-only">Eduard Belsky Online Art Gallery</h1>
            <div>
                <SlideBanner>
                    <div className="flex flex-col gap-4 w-full h-full justify-center">
                        <span className="text-5xl font-bold">{t('Welcome to Online Art Gallery')}</span>
                        <span className="text-2xl">{t('of Eduard Belsky')}</span>
                        <Link to={routes.works.index} className='max-w-fit'>
                            <Button title={t('Shop all')} type="primary" styles="mt-4 hover:text-black hover:bg-white" />
                        </Link>
                    </div>
                </SlideBanner>
                <Inner>
                    <div className="flex flex-col sm:flex-row gap-4 p-12 justify-between items-start">
                        {/* Mobile intro block */}
						<div className="block sm:hidden w-full">
							<div className="flex flex-col items-center text-left gap-4">
								<span className="text-3xl font-medium">{t('Hello and welcome!')}</span>
								<p className="pt-7 text-lg">
									{t('Why launch my own website, and why now?')}
									<br />
									{t('The answer is simple: to connect with you directly and share what I create — in a modern, convenient, and user-friendly way.')}
								</p>
								<p className="pt-5 text-lg">
									{t('Here you can easily order my paintings, explore the catalog (including older works), and even see some of them being made through short videos.')}
									<br />
									{t('I’ve also added a bit of tech: with one click, you can view a painting in augmented reality — just download a small app and see how it would look on your wall. I think that’s pretty cool!')}
								</p>
								<p className="pt-5 text-lg">
									{t('Though I don’t often write about my art, some pieces include short descriptions.')}
									<br />
									{t('Shipping costs are now calculated automatically — no need to reach out just to find out the price.')}
								</p>
								<p className="pt-5 text-lg">
									{t('The site is available in several major languages. If yours isn’t listed yet, feel free to contact me — we’ll fix that.')}
								</p>
								<p className="pt-5 text-lg">
									{t('You’ll find a short bio below, but honestly, I prefer to let my work speak for itself.')}
								</p>
								<p className="pt-5 text-lg">
									{t('For any questions or feedback, use the contact form — or just message me directly if you already have my number.')}
									<br />
									{t('And if you’d like to stay updated, subscribe to the newsletter on the homepage. I won’t spam you — just an occasional email (1–2 times a month) with new works, so you can be the first to see them.')}
								</p>
								<p className="pt-5 text-lg">
									{t('Let’s wish each other luck — and enjoy the journey!')}
								</p>
								<img
									src="/img/portrait.jpg"
									alt={t('Eduard Belsky portrait')}
									className="w-3/4 max-w-xs rounded-lg mx-auto my-6 object-cover"
								/>
                                <div className="w-full border-t border-slate-300 my-4" />
							</div>
						</div>
						{/* Desktop: left column */}
						<div className="hidden sm:flex justify-center flex-col w-full sm:w-1/2">
							<span className="text-5xl sm:text-2xl font-medium mb-4">{t('Hello and welcome!')}</span>
							<p className="pt-7 text-lg">
								{t('Why launch my own website, and why now?')}
								<br />
								{t('The answer is simple: to connect with you directly and share what I create — in a modern, convenient, and user-friendly way.')}
							</p>
							<p className="pt-5 text-lg">
								{t('Here you can easily order my paintings, explore the catalog (including older works), and even see some of them being made through short videos.')}
								<br />
								{t('I’ve also added a bit of tech: with one click, you can view a painting in augmented reality — just download a small app and see how it would look on your wall. I think that’s pretty cool!')}
							</p>
							<p className="pt-5 text-lg">
								{t('Though I don’t often write about my art, some pieces include short descriptions.')}
								<br />
								{t('Shipping costs are now calculated automatically — no need to reach out just to find out the price.')}
							</p>
							<p className="pt-5 text-lg">
								{t('The site is available in several major languages. If yours isn’t listed yet, feel free to contact me — we’ll fix that.')}
							</p>
							<p className="pt-5 text-lg">
								{t('You’ll find a short bio below, but honestly, I prefer to let my work speak for itself.')}
							</p>
							<p className="pt-5 text-lg">
								{t('For any questions or feedback, use the contact form — or just message me directly if you already have my number.')}
								<br />
								{t('And if you’d like to stay updated, subscribe to the newsletter on the homepage. I won’t spam you — just an occasional email (1–2 times a month) with new works, so you can be the first to see them.')}
							</p>
							<p className="pt-5 text-lg">
								{t('Let’s wish each other luck — and enjoy the journey!')}
							</p>
							<div className="w-full border-t border-slate-300 my-4" />
							<span className="text-5xl sm:text-2xl font-medium">{t('About Me')}</span>
							<p className="pt-7 text-lg">{t('Eduard Belsky was born in 1963 in Colki, Volyn region, Ukraine.')}</p>
							<p className="pt-5 text-lg">{t('He studied at the Republican Art School in Kyiv and the Dnipro Art School, and graduated from the Ukrainian Academy of Arts (Faculty of Monumental Art) in 1991. Since 1992, he has been a member of the Art Union of Painters of Ukraine.')}</p>
							<p className="pt-5 text-lg">{t('He lives and works in Izola, Slovenia, and collaborates with Ukrainian and European galleries.')}</p>
							<p className="pt-5 text-lg">{t('Belsky is passionate about life, and his bright, positive outlook shines through in his paintings. He immerses himself in the process like meditation, letting imagination and spirit take flight.')}</p>
						</div>
                        {/* Desktop: right column image and newsletter */}
                        <div className="hidden sm:flex flex-col items-center sm:w-2/5 mt-10 sm:mt-0">
                            <img
                                src="/img/portrait.jpg"
                                alt={t('Eduard Belsky portrait')}
                                className="rounded-lg object-contain w-full"
                            />
                            {/* Newsletter form for desktop */}
                            <div className="w-full mt-6 bg-orange-100 rounded-xl px-8 py-10 flex flex-col items-center gap-4">
                                <span className="text-xl font-semibold text-orange-900 text-left">
                                    {t('Updates from me - new works and the thoughts behind them.')}
                                </span>
                                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="w-full py-1 px-4 rounded-full text-lg text-black border border-orange-300"
                                        placeholder={t('Email')}
                                        required
                                    />
                                    <button
                                        type="submit"
                                        className="bg-orange-400 hover:bg-orange-500 text-white rounded-full px-6 py-2 text-lg font-semibold transition"
                                    >
                                        {t('get inspired')}
                                    </button>
                                </form>
                                {message && <div className="text-sm text-orange-900">{message}</div>}
                            </div>
                        </div>
                        {/* Mobile: About Me block */}
                        <div className="block sm:hidden w-full">
                            <span className="text-3xl font-medium">{t('About Me')}</span>
                            <p className="pt-7 text-lg">{t('Eduard Belsky was born in 1963 in Colki, Volyn region, Ukraine.')}</p>
                            <p className="pt-5 text-lg">{t('He studied at the Republican Art School in Kyiv and the Dnipro Art School, and graduated from the Ukrainian Academy of Arts (Faculty of Monumental Art) in 1991. Since 1992, he has been a member of the Art Union of Painters of Ukraine.')}</p>
                            <p className="pt-5 text-lg">{t('He lives and works in Izola, Slovenia, and collaborates with Ukrainian and European galleries.')}</p>
                            <p className="pt-5 text-lg">{t('Belsky is passionate about life, and his bright, positive outlook shines through in his paintings. He immerses himself in the process like meditation, letting imagination and spirit take flight.')}</p>
							{/* Newsletter form for mobile */}
						<div className="w-full mt-4 bg-orange-100 rounded-xl px-6 py-8 flex flex-col items-center gap-4">
							<span className="text-xl font-semibold text-orange-900 text-left">
								{t('Updates from me — new works and the thoughts behind them.')}
							</span>
							<form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
								<input
									type="email"
									value={email}
									onChange={e => setEmail(e.target.value)}
									className="w-full py-1 px-4 rounded-full text-lg text-black border border-orange-300"
									placeholder={t('Email')}
									required
								/>
								<button
									type="submit"
									className="bg-orange-400 hover:bg-orange-500 text-white rounded-full px-6 py-2 text-lg font-semibold transition"
								>
									{t('get inspired')}
								</button>
							</form>
							{message && <div className="text-sm text-orange-900">{message}</div>}
						</div>
                        </div>
                    </div>
                </Inner>
            </div>
			<script type="application/ld+json">
				{`
				{
				"@context": "https://schema.org",
				"@type": "WebSite",
				"name": "Belsky Art Gallery",
				"url": "https://eduardbelsky.com/"
				}
				`}
			</script>
        </>
    )
}
export default Main